import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Tag } from 'antd';
import { ExclamationCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';

const Pill = styled(Tag)`
	cursor: pointer;
	margin: 0;
	padding: 0;
	font-size: 13px;
	font-weight: 500;
	/* Never absorb flex shrink — the count is the whole point. */
	flex: 0 0 auto;
	white-space: nowrap;
	background: transparent;
	border: none;
	color: #b45309;

	&:hover {
		color: #92400e;
		text-decoration: underline;
	}
`;

const ReasonRow = styled(Flex)`
	justify-content: space-between;
	align-items: baseline;
	padding: 8px 0;
	border-bottom: 1px dashed #e5e7eb;
	font-size: 14px;

	&:last-of-type {
		border-bottom: none;
	}
`;

const Count = styled.span`
	font-weight: 600;
	color: #111827;
`;

export const UNFETCHED_REASONS = (context) => [
	{ key: 'does_not_exist', label: 'Does not exist', column: 'Does Not Exist' },
	{ key: 'invalid', label: 'Invalid Trip ID', column: 'Invalid' },
	{ key: 'not_closed', label: 'Not ready for billing', column: 'Not Ready For Billing' },
	{
		key: 'out_of_scope',
		label:
			context === 'vendor'
				? 'Does not belong to vendor'
				: 'Does not belong to selected client/project',
		column:
			context === 'vendor'
				? 'Does Not Belong To Vendor'
				: 'Does Not Belong To Client/Project',
	},
	{ key: 'already_billed', label: 'Already invoiced', column: 'Already Invoiced' },
];

const csvCell = (value) => {
	const text = value == null ? '' : String(value);
	return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const exportUnfetchedTrips = (unfetched, context) => {
	const reasons = UNFETCHED_REASONS(context);
	const columns = reasons.map((r) => unfetched?.[r.key] || []);
	const depth = Math.max(0, ...columns.map((c) => c.length));

	const lines = [reasons.map((r) => csvCell(r.column)).join(',')];
	for (let i = 0; i < depth; i += 1) {
		lines.push(columns.map((c) => csvCell(c[i] || '')).join(','));
	}

	const blob = new Blob([`${lines.join('\n')}\n`], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `unfetched-trip-ids-${context}.csv`;
	link.click();
	URL.revokeObjectURL(url);
};

export default function UnfetchedTripsPill({ bulkResult, context }) {
	const [open, setOpen] = useState(false);

	const total = bulkResult?.unfetched_count || 0;
	if (!bulkResult || total === 0) return null;

	const reasons = UNFETCHED_REASONS(context);
	const counts = bulkResult.counts || {};

	return (
		<>
			<Pill icon={<ExclamationCircleOutlined />} onClick={() => setOpen(true)}>
				Unfetched ({total})
			</Pill>

			<Modal
				open={open}
				title="Unfetched Trip IDs"
				onCancel={() => setOpen(false)}
				destroyOnClose
				width={480}
				centered
				footer={[
					<Button key="close" onClick={() => setOpen(false)}>
						Close
					</Button>,
					<Button
						key="export"
						type="primary"
						icon={<DownloadOutlined />}
						onClick={() => exportUnfetchedTrips(bulkResult.unfetched, context)}
					>
						Export Trip IDs
					</Button>,
				]}
			>
				<Box>
					{reasons.map((reason) => (
						<ReasonRow key={reason.key}>
							<Text>{reason.label}</Text>
							<Count>{counts[reason.key] || 0}</Count>
						</ReasonRow>
					))}
				</Box>

				<Box mt={3}>
					<Text style={{ fontSize: 12, color: '#6b7280' }}>
						{bulkResult.fetched_count} of {bulkResult.total_uploaded} uploaded Trip IDs
						were fetched
						{bulkResult.duplicates_removed
							? `, ${bulkResult.duplicates_removed} duplicate${
									bulkResult.duplicates_removed > 1 ? 's' : ''
							  } ignored`
							: ''}
						. Re-upload the full corrected list to try again — each bulk fetch replaces
						the previous result.
					</Text>
				</Box>
			</Modal>
		</>
	);
}

UnfetchedTripsPill.propTypes = {
	bulkResult: PropTypes.shape({
		unfetched_count: PropTypes.number,
		fetched_count: PropTypes.number,
		total_uploaded: PropTypes.number,
		duplicates_removed: PropTypes.number,
		counts: PropTypes.object,
		unfetched: PropTypes.object,
	}),
	context: PropTypes.oneOf(['vendor', 'client']).isRequired,
};

UnfetchedTripsPill.defaultProps = {
	bulkResult: null,
};
